import type { SlideBackground } from '../../types/api';

/**
 * The item templates' own geometry, as much of it as the preview needs.
 *
 * Mirrors of app/rendering/spec.py and render.py: the portrait discs, the
 * split-panel profile composition, the grid cells, the menu rows and the offer
 * rows. Everything is a fraction of the 1080 canvas, the unit the rest of the
 * stage already speaks — change one side, change the other. Like the whole
 * stage this is an approximation: browser fonts, no autofit ladder, and the
 * "preview approximate" line under it stays the truth.
 */

const CANVAS_SIZE = 1080;

function ofCanvas(pixels: number): number {
  return Math.round((pixels / CANVAS_SIZE) * 10000) / 10000;
}

/* The hero portrait (profile on a photo, and the community portrait zone). */
export const PORTRAIT_DIAMETER = ofCanvas(380);
export const PORTRAIT_GAP = ofCanvas(40);
export const PORTRAIT_RING = ofCanvas(8);

/* The designation line: a short accent rule, then tracked caps. */
export const PROFILE_DIVIDER_WIDTH = ofCanvas(56);
export const PROFILE_DIVIDER_HEIGHT = ofCanvas(3);
export const SUBTITLE_FONT = ofCanvas(26);

/* The grid (profile_grid): small discs in 2 or 3 columns. */
export const GRID_PORTRAIT_DIAMETER = ofCanvas(200);
export const GRID_NARROW_MAX_ITEMS = 4;
export const GRID_CELL_GAP_X = ofCanvas(48);
export const GRID_CELL_GAP_Y = ofCanvas(44);
export const GRID_NAME_FONT = ofCanvas(30);
export const GRID_SUBTITLE_FONT = ofCanvas(20);
export const GRID_TOP_GAP = ofCanvas(44);

/* The menu rows: dish, dotted leader, price. */
export const MENU_ROW_TITLE_FONT = ofCanvas(36);
export const MENU_ROW_SUBTITLE_FONT = ofCanvas(26);
export const MENU_ROW_GAP = ofCanvas(28);
export const MENU_ROWS_GAP = ofCanvas(44);

/* The offer's benefit rows: an accent tick, the benefit, an optional value. */
export const OFFER_ROW_FONT = ofCanvas(34);
export const OFFER_ROWS_GAP = ofCanvas(36);
export const OFFER_TICK_GAP = ofCanvas(18);

/* The split-panel profile (profile + item on a flat brand background). */
export const SPLIT_PANEL_FRACTION = 0.44;
export const SPLIT_PORTRAIT_DIAMETER = ofCanvas(400);
export const SPLIT_PORTRAIT_CENTER_Y = ofCanvas(470);
export const SPLIT_TEXT_GUTTER = ofCanvas(72);
export const SPLIT_EDGE_BAR_WIDTH = ofCanvas(14);
export const SPLIT_NAME_FIRST_FONT = ofCanvas(100);
export const SPLIT_NAME_REST_FONT = ofCanvas(64);
export const SPLIT_NAME_LINE_GAP = ofCanvas(6);
export const SPLIT_CALLOUT_NUMBER_FONT = ofCanvas(110);
export const SPLIT_CALLOUT_UNIT_FONT = ofCanvas(30);
export const SPLIT_CALLOUT_GAP = ofCanvas(44);

/** The split composition's type column: left edge and width, canvas fractions. */
export const SPLIT_COLUMN_LEFT = SPLIT_PANEL_FRACTION + SPLIT_TEXT_GUTTER;
export const SPLIT_COLUMN_WIDTH = 1 - SPLIT_COLUMN_LEFT - SPLIT_TEXT_GUTTER - SPLIT_EDGE_BAR_WIDTH;

/* The anniversary poster (mirrors the ANNIV_* block in spec.py). */
export const ANNIV_PHOTO_LEFT = ofCanvas(30);
export const ANNIV_PHOTO_TOP = ofCanvas(250);
export const ANNIV_PHOTO_WIDTH = ofCanvas(580);

/** The template's own photo box — what a null `photo_box` renders as. */
export const ANNIV_DEFAULT_PHOTO_BOX = {
  x: ANNIV_PHOTO_LEFT,
  y: ANNIV_PHOTO_TOP,
  w: ANNIV_PHOTO_WIDTH,
  h: 1 - ANNIV_PHOTO_TOP,
} as const;

/** The smallest box the editor will commit; the renderer floors at 80px too. */
export const ANNIV_PHOTO_MIN_SIZE = 0.15;
export const ANNIV_TEXT_LEFT = ofCanvas(610);
export const ANNIV_TEXT_WIDTH = ofCanvas(390);
export const ANNIV_NAME_TOP = ofCanvas(260);
export const ANNIV_NAME_FIRST_FONT = ofCanvas(72);
export const ANNIV_NAME_REST_FONT = ofCanvas(96);
export const ANNIV_QUOTE_FONT = ofCanvas(34);
export const ANNIV_QUOTE_RULE_WIDTH = ofCanvas(36);
export const ANNIV_BADGE_LEFT = ofCanvas(80);
export const ANNIV_BADGE_BOTTOM = ofCanvas(70);
export const ANNIV_BADGE_TITLE_FONT = ofCanvas(30);
export const ANNIV_BADGE_SUB_FONT = ofCanvas(24);
export const ANNIV_ARC_CENTER = { x: 0.82, y: 0.86 };
export const ANNIV_ARC_RADII: readonly number[] = [330 / 1080, 240 / 1080, 150 / 1080];
export const ANNIV_ARC_WIDTH = ofCanvas(34);
export const ANNIV_ARC_ALPHA = 70 / 255;

/**
 * The anniversary badge's plate, mirroring the renderer: the field's own hue
 * pulled darker on a light field, lighter on a dark one.
 */
function anniversaryBadgeBase(background: SlideBackground): [number, number, number] {
  const base =
    background.kind === 'color'
      ? rgb(background.color)
      : background.kind === 'gradient'
        ? blend(rgb(background.from), rgb(background.to), 0.5)
        : background.kind === 'pattern'
          ? rgb(background.ground)
          : rgb('#2B2A28');
  const luminance = (0.2126 * base[0] + 0.7152 * base[1] + 0.0722 * base[2]) / 255;
  return luminance > 0.45 ? blend(base, [0, 0, 0], 0.24) : blend(base, [255, 255, 255], 0.16);
}

export function anniversaryBadgeFill(background: SlideBackground): string {
  return rgbCss(anniversaryBadgeBase(background));
}

/** Ink that reads on the badge's plate — the renderer's own light/dark pick. */
export function anniversaryBadgeInk(background: SlideBackground): string {
  const [red, green, blue] = anniversaryBadgeBase(background);
  const luminance = (0.2126 * red + 0.7152 * green + 0.0722 * blue) / 255;
  return luminance > 0.5 ? '#1C1B18' : '#F7F5F0';
}

/* The flat-background decoration (glow + rings) behind an item composition. */
export const ITEM_RING_RADII: readonly number[] = [0.42, 0.62];
export const ITEM_RING_ALPHAS: readonly number[] = [46 / 255, 26 / 255];
export const ITEM_GLOW_ALPHA = 64 / 255;
export const ITEM_GLOW_SCALE = 1.15;
export const ITEM_FOCAL_POINTS: Readonly<Record<string, { x: number; y: number }>> = {
  profile: { x: 0.5, y: 0.4 },
  profile_grid: { x: 0.5, y: 0.44 },
  menu_list: { x: 0.5, y: 0.3 },
  offer: { x: 0.5, y: 0.42 },
};

/** `#RRGGBB` → channels, forgiving of anything unparseable (the renderer's own stance). */
export function rgb(hex: string): [number, number, number] {
  const value = hex.replace('#', '');
  const red = Number.parseInt(value.slice(0, 2), 16);
  const green = Number.parseInt(value.slice(2, 4), 16);
  const blue = Number.parseInt(value.slice(4, 6), 16);
  if (Number.isNaN(red) || Number.isNaN(green) || Number.isNaN(blue)) return [43, 42, 40];
  return [red, green, blue];
}

export function rgbCss(channels: [number, number, number], alpha = 1): string {
  return alpha >= 1
    ? `rgb(${channels[0]}, ${channels[1]}, ${channels[2]})`
    : `rgba(${channels[0]}, ${channels[1]}, ${channels[2]}, ${Math.round(alpha * 100) / 100})`;
}

/** Linear blend of two colours; factor 0 keeps `base`. Mirrors `_blend`. */
export function blend(
  base: [number, number, number],
  toward: [number, number, number],
  factor: number,
): [number, number, number] {
  const clamped = Math.min(Math.max(factor, 0), 1);
  return [
    Math.round(base[0] + (toward[0] - base[0]) * clamped),
    Math.round(base[1] + (toward[1] - base[1]) * clamped),
    Math.round(base[2] + (toward[2] - base[2]) * clamped),
  ];
}

/**
 * True when this background takes the item composition (split panel, decor):
 * no picture behind it. A pattern counts, its ground standing in for the
 * flat colour, as a gradient does.
 */
export function isFlatBackground(background: SlideBackground): boolean {
  return (
    background.kind === 'color' || background.kind === 'gradient' || background.kind === 'pattern'
  );
}

/**
 * The split composition's two colour roles, mirroring `_render_profile_split`:
 * the field is the background's own colour, the panel the gradient's far stop —
 * or the field pulled toward ink when there is only one colour to work with.
 */
export function splitColors(background: SlideBackground): {
  field: [number, number, number];
  panel: [number, number, number];
} {
  if (background.kind === 'gradient') {
    const field = rgb(background.from);
    const panel = rgb(background.to);
    if (panel[0] !== field[0] || panel[1] !== field[1] || panel[2] !== field[2]) {
      return { field, panel };
    }
    return { field, panel: blend(field, [0, 0, 0], 0.3) };
  }
  const field =
    background.kind === 'color'
      ? rgb(background.color)
      : background.kind === 'pattern'
        ? rgb(background.ground)
        : rgb('#2B2A28');
  return { field, panel: blend(field, [0, 0, 0], 0.3) };
}

/**
 * A value string as the achievement callout draws it: "3 years" splits into a
 * display numeral and a tracked-caps unit; anything unnumbered is one line.
 */
export function splitValue(value: string): { number: string; unit: string } {
  const trimmed = value.trim();
  const parts = trimmed.split(/\s+/);
  const first = parts[0] ?? '';
  if (first.length > 0 && /^\d+$/.test(first)) {
    return { number: first, unit: parts.slice(1).join(' ').toUpperCase() };
  }
  return { number: trimmed.toUpperCase(), unit: '' };
}

/** Up to two initials from a name ("Sarah Chen" → "SC"), for the placeholder disc. */
export function initials(title: string): string {
  return title
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0] ?? '')
    .join('')
    .toUpperCase();
}
